import {messages} from '@smartmixin/i18n';
import {Helmet} from 'react-helmet-async';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router';

type HreflangAlternate = {hrefLang: string; href: string};

type SmartMixinHelmetProps = {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
  /**
   * Bidirectional hreflang cluster for pages that exist in several languages
   * at distinct URLs (the help center). Every language version must emit the
   * identical cluster, itself included. See useHelpAlternates().
   */
  alternates?: HreflangAlternate[];
};

const DEFAULT_IMAGE = 'https://smartmixin.io/og-image.png';
const SITE_NAME = 'SmartMixin';

const SmartMixinHelmet = ({
  title,
  description,
  image,
  type = 'website',
  noIndex = false,
  alternates,
}: SmartMixinHelmetProps) => {
  const {locale, formatMessage} = useIntl();
  const {pathname} = useLocation();

  const fullTitle = title ? `${title} - ${SITE_NAME}` : SITE_NAME;
  const fullDescription =
    description || formatMessage(messages.landing.heroDescription);
  const fullUrl = `${import.meta.env.VITE_FRONTEND_URL}${pathname}`;
  const fullImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <html lang={locale} />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <link rel="canonical" href={fullUrl} />

      {alternates?.map((alternate) => (
        <link
          key={alternate.hrefLang}
          rel="alternate"
          hrefLang={alternate.hrefLang}
          href={alternate.href}
        />
      ))}

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullImage} />
    </Helmet>
  );
};

export default SmartMixinHelmet;
